import { getReferenceString, MedplumClient } from '@medplum/core';
import { Task } from '@medplum/fhirtypes';
import { ORBIT_PROVIDER_TASK_GROUP_IDENTIFIER } from './codings';

/**
 * The Care Coordinator tasks the PN card's coverage next step creates (BAR-2921,
 * BAR-2922). The title is stored as the Task's description. bariendo-be mirrors
 * these to stage the portal the same way (BAR-3002).
 */
export const EMPLOYER_VERIFICATION_TASK_TITLE = 'Confirm employer benefits';
export const INSURANCE_VERIFICATION_TASK_TITLE = 'Verify insurance and start prior auth';
export const COVERAGE_VERIFICATION_TASK_TITLES: readonly string[] = [
  EMPLOYER_VERIFICATION_TASK_TITLE,
  INSURANCE_VERIFICATION_TASK_TITLE,
];

/** Task statuses that mean someone still has it to do. */
export const OPEN_TASK_STATUSES: readonly Task['status'][] = [
  'draft',
  'requested',
  'received',
  'accepted',
  'ready',
  'in-progress',
  'on-hold',
];

/** Whether the task is an open coverage verification task. */
export function isOpenCoverageVerificationTask(task: Task): boolean {
  return (
    OPEN_TASK_STATUSES.includes(task.status) &&
    task.groupIdentifier?.value === ORBIT_PROVIDER_TASK_GROUP_IDENTIFIER.value &&
    COVERAGE_VERIFICATION_TASK_TITLES.includes(task.description ?? '')
  );
}

/**
 * Whether the Care Coordinators have a coverage verification task open for the
 * patient: with an attended consult, the patient is in Financing and approval
 * (BAR-3002). Throws when the read fails, so a stage isn't computed from half the facts.
 */
export async function hasOpenCoverageVerificationTask(medplum: MedplumClient, patientId: string): Promise<boolean> {
  const tasks = await medplum.searchResources('Task', {
    subject: getReferenceString({ resourceType: 'Patient', id: patientId }),
    status: OPEN_TASK_STATUSES.join(','),
    // The value alone, as the access policies' criteria and the inbox search it.
    'group-identifier': ORBIT_PROVIDER_TASK_GROUP_IDENTIFIER.value,
    _count: 100,
  });
  return tasks.some(isOpenCoverageVerificationTask);
}
